<template>
  <div
    :class="{ 'has-seconds': showSeconds }"
    class="el-time-spinner"
  >
    <template v-if="!arrowControl">
      <el-scrollbar
        ref="hours"
        class="el-time-spinner__wrapper"
        wrap-style="max-height: inherit;"
        view-class="el-time-spinner__list"
        noresize
        tag="ul"
        @mouseenter.native="emitSelectRange('hours')"
        @mousemove.native="adjustCurrentSpinner('hours')"
      >
        <li
          v-for="(disabled, hour) in hoursList"
          :key="hour"
          :class="{ active: hour === hours, disabled: disabled }"
          class="el-time-spinner__item"
          @click="handleClick('hours', { value: hour, disabled: disabled })"
        >
          {{ ('0' + (amPmMode ? hour % 12 || 12 : hour)).slice(-2) }}{{ amPm(hour) }}
        </li>
      </el-scrollbar>
      <el-scrollbar
        ref="minutes"
        class="el-time-spinner__wrapper"
        wrap-style="max-height: inherit;"
        view-class="el-time-spinner__list"
        noresize
        tag="ul"
        @mouseenter.native="emitSelectRange('minutes')"
        @mousemove.native="adjustCurrentSpinner('minutes')"
      >
        <li
          v-for="(enabled, key) in minutesList"
          :key="key"
          :class="{
            active: _computedMinutesStepper
              ? key * _computedMinutesStepper ===
                Math.floor(minutes / _computedMinutesStepper) * _computedMinutesStepper
              : key === minutes,
            disabled: !enabled
          }"
          class="el-time-spinner__item"
          @click="handleClick('minutes', { value: key, disabled: false })"
        >
          <span v-if="_computedMinutesStepper">{{ enabled }}</span>
          <span v-else>{{ ('0' + key).slice(-2) }}</span>
        </li>
      </el-scrollbar>
      <el-scrollbar
        v-show="showSeconds"
        ref="seconds"
        class="el-time-spinner__wrapper"
        wrap-style="max-height: inherit;"
        view-class="el-time-spinner__list"
        noresize
        tag="ul"
        @mouseenter.native="emitSelectRange('seconds')"
        @mousemove.native="adjustCurrentSpinner('seconds')"
      >
        <li
          v-for="(second, key) in secondsList"
          :key="key"
          :class="{
            active: _computedSecondStepper
              ? key * _computedSecondStepper === Math.floor(seconds / _computedSecondStepper) * _computedSecondStepper
              : key === seconds
          }"
          class="el-time-spinner__item"
          @click="handleClick('seconds', { value: key, disabled: false })"
        >
          <span v-if="_computedSecondStepper">{{ second }}</span>
          <span v-else>{{ ('0' + key).slice(-2) }}</span>
        </li>
      </el-scrollbar>
    </template>
    <template v-if="arrowControl">
      <div
        class="el-time-spinner__wrapper is-arrow"
        @mouseenter="emitSelectRange('hours')"
      >
        <i
          v-repeat-click="decrease"
          class="el-time-spinner__arrow el-icon-arrow-up"
        ></i>
        <i
          v-repeat-click="increase"
          class="el-time-spinner__arrow el-icon-arrow-down"
        ></i>
        <ul
          ref="hours"
          class="el-time-spinner__list"
        >
          <li
            v-for="(hour, key) in arrowHourList"
            :key="key"
            :class="{ active: hour === hours, disabled: hoursList[hour] }"
            class="el-time-spinner__item"
          >
            {{ hour === undefined ? '' : ('0' + (amPmMode ? hour % 12 || 12 : hour)).slice(-2) + amPm(hour) }}
          </li>
        </ul>
      </div>
      <div
        class="el-time-spinner__wrapper is-arrow"
        @mouseenter="emitSelectRange('minutes')"
      >
        <i
          v-repeat-click="decrease"
          class="el-time-spinner__arrow el-icon-arrow-up"
        ></i>
        <i
          v-repeat-click="increase"
          class="el-time-spinner__arrow el-icon-arrow-down"
        ></i>
        <ul
          ref="minutes"
          class="el-time-spinner__list"
        >
          <li
            v-for="(minute, key) in arrowMinuteList"
            :key="key"
            :class="{ active: minute === minutes }"
            class="el-time-spinner__item"
          >
            {{ minute === undefined ? '' : ('0' + minute).slice(-2) }}
          </li>
        </ul>
      </div>
      <div
        v-if="showSeconds"
        class="el-time-spinner__wrapper is-arrow"
        @mouseenter="emitSelectRange('seconds')"
      >
        <i
          v-repeat-click="decrease"
          class="el-time-spinner__arrow el-icon-arrow-up"
        ></i>
        <i
          v-repeat-click="increase"
          class="el-time-spinner__arrow el-icon-arrow-down"
        ></i>
        <ul
          ref="seconds"
          class="el-time-spinner__list"
        >
          <li
            v-for="(second, key) in arrowSecondList"
            :key="key"
            :class="{ active: second === seconds }"
            class="el-time-spinner__item"
          >
            {{ second === undefined ? '' : ('0' + second).slice(-2) }}
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
import { getRangeHours, getRangeMinutes, modifyTime } from 'element-ui/lib/utils/date-util';
import ElScrollbar from '../../scrollbar';
import RepeatClick from 'element-ui/lib/directives/repeat-click';

export default {
  components: { ElScrollbar },

  directives: {
    repeatClick: RepeatClick
  },

  props: {
    // eslint-disable-next-line vue/require-prop-types
    date: {},
    // eslint-disable-next-line vue/require-prop-types
    defaultValue: {}, // reserved for future use
    showSeconds: {
      type: Boolean,
      default: true
    },
    arrowControl: Boolean,
    amPmMode: {
      type: String,
      default: '' // 'a': am/pm; 'A': AM/PM
    },
    // 分钟 / 秒跨度
    stepper: {
      type: [Number, Boolean],
      default: undefined
    }
  },

  data () {
    return {
      selectableRange: [],
      currentScrollbar: null
    };
  },

  computed: {
    /**
     * @private 分钟跨度
     */
    _computedMinutesStepper () {
      if (!this.showSeconds) return this._getStepper;
      else return false;
    },
    /**
     * @private 秒跨度
     */
    _computedSecondStepper () {
      if (this.showSeconds) return this._getStepper;
      else return false;
    },
    /**
     * @private 跨度
     * 1. stepper 为 Number 取值当前所传值
     * 2. stepper 为 Boolean 取值 10
     */
    _getStepper () {
      if (typeof this.stepper === 'number') {
        return this.stepper
      }
      return this.stepper ? 10 : 0;
    },
    hours () {
      return this.date.getHours();
    },
    minutes () {
      return this.date.getMinutes();
    },
    seconds () {
      return this.date.getSeconds();
    },
    hoursList () {
      return getRangeHours(this.selectableRange);
    },
    minutesList () {
      if (this._computedMinutesStepper) {
        return this.getStepMinutes();
      } else {
        return getRangeMinutes(this.selectableRange, this.hours);
      }
    },
    secondsList () {
      if (this._computedSecondStepper) {
        return this.getStepMinutes();
      } else {
        return 60;
      }
    },
    arrowHourList () {
      const hours = this.hours;
      return [hours > 0 ? hours - 1 : undefined, hours, hours < 23 ? hours + 1 : undefined];
    },
    arrowMinuteList () {
      const minutes = this.minutes;
      return [minutes > 0 ? minutes - 1 : undefined, minutes, minutes < 59 ? minutes + 1 : undefined];
    },
    arrowSecondList () {
      const seconds = this.seconds;
      return [seconds > 0 ? seconds - 1 : undefined, seconds, seconds < 59 ? seconds + 1 : undefined];
    }
  },

  mounted () {
    this.$nextTick(() => {
      !this.arrowControl && this.bindScrollEvent();
    });
  },

  methods: {
    times (n, iteratee, stepper) {
      let index = 0;
      const result = [];
      while (index < n) {
        result.push(iteratee(index));
        index = index + stepper;
      }
      return result;
    },
    /**
     * @private 时间跨度获取分钟方法
     */
    getStepMinutes () {
      return this.times(
        60,
        index => {
          let value = index;
          value = value < 10 ? `0${value}` : `${value}`;
          return value;
        },
        this.showSeconds ? this._computedSecondStepper : this._computedMinutesStepper
      );
    },
    increase () {
      this.scrollDown(1);
    },

    decrease () {
      this.scrollDown(-1);
    },

    modifyDateField (type, value) {
      switch (type) {
        case 'hours':
          this.$emit('change', modifyTime(this.date, value, this.minutes, this.seconds));
          break;
        case 'minutes':
          this.$emit('change', modifyTime(this.date, this.hours, value, this.seconds));
          break;
        case 'seconds':
          this.$emit('change', modifyTime(this.date, this.hours, this.minutes, value));
          break;
      }
    },

    handleClick (type, { value, disabled }) {
      if (!disabled) {
        if (this._computedMinutesStepper && type === 'minutes') {
          this.modifyDateField(type, this._computedMinutesStepper * value);
        } else if (this._computedSecondStepper && type === 'seconds') {
          this.modifyDateField(type, this._computedSecondStepper * value);
        } else {
          this.modifyDateField(type, value);
        }
        this.emitSelectRange(type);
        this.adjustSpinner(type, value);
      }
    },

    emitSelectRange (type) {
      if (type === 'hours') {
        this.$emit('select-range', 0, 2);
      } else if (type === 'minutes') {
        this.$emit('select-range', 3, 5);
      } else if (type === 'seconds') {
        this.$emit('select-range', 6, 8);
      }
      this.currentScrollbar = type;
    },

    bindScrollEvent () {
      const bindFuntion = type => {
        this.$refs[type].wrap.onscroll = e => {
          // scroll is emitted when set scrollTop programatically
          // should find better solutions in the future!
          this.handleScroll(type, e);
        };
      };
      bindFuntion('hours');
      bindFuntion('minutes');
      bindFuntion('seconds');
    },

    handleScroll (type) {
      const value = Math.min(
        Math.round(
          (this.$refs[type].wrap.scrollTop - (this.scrollBarHeight(type) * 0.5 - 10) / this.typeItemHeight(type) + 3) /
            this.typeItemHeight(type)
        ),
        type === 'hours' ? 23 : 59
      );
      if (this._computedMinutesStepper && type === 'minutes') {
        this.modifyDateField(type, this._computedMinutesStepper * value);
      } else if (this._computedSecondStepper && type === 'seconds') {
        this.modifyDateField(type, this._computedSecondStepper * value);
      } else {
        this.modifyDateField(type, value);
      }
    },

    // NOTE: used by datetime / date-range panel
    //       renamed from adjustScrollTop
    //       should try to refactory it
    adjustSpinners () {
      this.adjustSpinner('hours', this.hours);
      if (this._computedMinutesStepper) {
        let stepIndex = Math.floor(this.minutes / this._computedMinutesStepper);
        this.adjustSpinner('minutes', stepIndex);
      } else {
        this.adjustSpinner('minutes', this.minutes);
      }
      if (this._computedSecondStepper) {
        let stepIndex = Math.floor(this.seconds / this._computedSecondStepper);
        this.adjustSpinner('seconds', stepIndex);
      } else {
        this.adjustSpinner('seconds', this.seconds);
      }
    },

    adjustCurrentSpinner (type) {
      if (this._computedMinutesStepper && type === 'minutes') {
        this.adjustSpinner(type, Math.floor(this.minutes / this._computedMinutesStepper));
      } else if (this._computedSecondStepper && type === 'seconds') {
        this.adjustSpinner(type, Math.floor(this.seconds / this._computedSecondStepper));
      } else {
        this.adjustSpinner(type, this[type]);
      }
    },

    adjustSpinner (type, value) {
      if (this.arrowControl) return;
      const el = this.$refs[type].wrap;
      if (el) {
        el.scrollTop = Math.max(0, value * this.typeItemHeight(type));
      }
    },

    scrollDown (step) {
      if (!this.currentScrollbar) {
        this.emitSelectRange('hours');
      }

      const label = this.currentScrollbar;
      const hoursList = this.hoursList;
      let now = this[label];

      if (this.currentScrollbar === 'hours') {
        let total = Math.abs(step);
        step = step > 0 ? 1 : -1;
        let length = hoursList.length;
        while (length-- && total) {
          now = (now + step + hoursList.length) % hoursList.length;
          if (hoursList[now]) {
            continue;
          }
          total--;
        }
        if (hoursList[now]) return;
      } else {
        let stepper = this._computedMinutesStepper || this._computedSecondStepper;
        if ((this._computedMinutesStepper && this.currentScrollbar === 'minutes') || (this._computedSecondStepper && this.currentScrollbar === 'seconds')) {
          now = (now + step * stepper + 60) % 60;
        } else {
          now = (now + step + 60) % 60;
        }
      }

      this.modifyDateField(label, now);
      this.adjustSpinner(label, now);
      this.$nextTick(() => this.emitSelectRange(this.currentScrollbar));
    },
    amPm (hour) {
      let shouldShowAmPm = this.amPmMode.toLowerCase() === 'a';
      if (!shouldShowAmPm) return '';
      let isCapital = this.amPmMode === 'A';
      let content = hour < 12 ? ' am' : ' pm';
      if (isCapital) content = content.toUpperCase();
      return content;
    },
    typeItemHeight (type) {
      return this.$refs[type].$el.querySelector('li').offsetHeight;
    },
    scrollBarHeight (type) {
      return this.$refs[type].$el.offsetHeight;
    }
  }
};
</script>



// WEBPACK FOOTER //
// time-spinner.vue?6a46b714